// src/core/hooks/useWodFiltering.ts
import { useMemo } from 'react';
import { Wod } from '../models/Wod';
import { filterWods } from '../utils/filters';

interface UseWodFilteringOptions {
  includeCompetitors?: boolean; // Si true, no filtra por type_id
  requireContent?: boolean; // Si true, requiere warm_up o blocks
}

export function useWodFiltering(wod: Wod[], boxId: number, options: UseWodFilteringOptions = {}) {
  const { includeCompetitors = false, requireContent = false } = options;

  // Mapeo de boxId -> type_ids de competidores
  const excludedTypeIds = useMemo(() => {
    if (includeCompetitors) return new Set<number>();

    const competitorTypeIdsByBox: Record<number, number[]> = {
      1: [4], // Box 1: excluir type_id = 4
      2: [8], // Box 2: excluir type_id = 8
      9: [12], // Box 9: excluir type_id = 12
    };

    const typeIdsToExclude = competitorTypeIdsByBox[boxId] || [];
    return new Set<number>(typeIdsToExclude);
  }, [boxId, includeCompetitors]);

  // Filtrar WODs según las opciones
  const filteredWods = useMemo(() => {
    if (!Array.isArray(wod) || wod.length === 0) return [];

    let result = wod;

    // Filtrar por type_id (competidores) si no se incluyen
    if (!includeCompetitors) {
      result = result.filter((w) => {
        const t = Number(w.type_id ?? 0);
        return !excludedTypeIds.has(t);
      });
    }

    // Aplicar filtro de competitors (por campo competitor)
    result = filterWods(result, !requireContent);

    // Filtrar por contenido si se requiere
    if (requireContent) {
      const beforeFilter = result.length;
      result = result.filter(
        (w) => (w.warm_up && w.warm_up !== '') || (Array.isArray(w.blocks) && w.blocks.length > 0),
      );
      // 🔍 DEBUG: Log de filtrado por contenido
      if (beforeFilter !== result.length) {
        console.log('[useWodFiltering DEBUG] WODs filtrados por contenido:', {
          antes: beforeFilter,
          despues: result.length,
          eliminados: beforeFilter - result.length,
          wodsEliminados: wod
            .filter((w) => !result.find((r) => r.id === w.id))
            .map((w) => ({
              id: w.id,
              title: w.title,
              warm_up: w.warm_up || 'VACÍO',
              blocksCount: w.blocks.length,
            })),
        });
      }
    }

    return result;
  }, [wod, excludedTypeIds, includeCompetitors, requireContent]);

  return {
    filteredWods,
    excludedTypeIds: includeCompetitors ? new Set<number>() : excludedTypeIds,
  };
}
